import { BaseListViewStore, DateTimeCellData, HyperLinkTrackingNumberCellData } from 'core/stores';
import { action, makeObservable, _getAdministration, _allowStateChanges } from 'mobx';
import { RemoveFiltersModalUtility, TableFilterUtility, notificationError } from 'core/utils';
import { BaasicTableSettingsViewStore } from 'common/stores';
import { LookupStore } from 'modules/lookup/stores';
import _ from 'lodash';
import ShipmentCostStore from './ShipmentCostStore';
import { ChargesCellData, ChargesDifferenceCellData, InvoiceNumberCellData } from 'core/stores/CellDataStore';
import { lookupRouteName } from 'common/constants';
import { localStorageHandler } from 'core/services';
import moment from 'moment';

class ShipmentCostListViewStore extends BaseListViewStore {
	noDataItem = true;
	showOnlyTotal = true;
	data = [];
	chargeTypesPresent = [];
	lastSearchFilter = null;

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ShipmentCostStore(rootStore),
			queryParams: {
				filter: localStorageHandler.get('shipment-cost-filter'),
				findFunction: 'findShipmentCostTn',
				ignore: true,
			},
		});
		this.defineTableData();
		makeObservable(this);
		this.parentViewStore = rootStore.shipmentCostViewStore;
		this.lookupStore = new LookupStore(rootStore);

		this.queryUtility.initialize(this.findShipmentCostTn);
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.tableFilter = new TableFilterUtility({
			lookupStore: new LookupStore(rootStore),
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [this.listStore.service],
		});
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		await this.lookupStore.find({ page: 1, rpp: 50 }, lookupRouteName.shipmentInfoChargeType);
		this.chargeTypeOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
	};

	@action.bound
	defineTableData = () => {
		this.tableData = {
			columns: [
				{
					key: 'trackingNumber',
					title: 'SHIPMENTCOST.TRACKINGNUMBER',
					dataCell: HyperLinkTrackingNumberCellData,
					carrierTypePath: 'carrier.carrierType.name',
					selected: true,
					service: 'shipmentInfoService',
					width: 200,
					filter: null,
					locked: true,
					filterApiParams: {
						shippedOnly: true,
					},
				},
				{
					key: 'shippingDate',
					title: 'SHIPMENTCOST.SHIPPEDDATE',
					dataCell: DateTimeCellData,
					dateFormat: 'L',
					selected: true,
					sortable: false,
					width: 120,
					filter: null,
					timezonePath: 'warehouseTimezone',
				},
				{
					key: 'invoiceNumber',
					title: 'SHIPMENTCOST.INVOICENUMBER',
					selected: true,
					sortable: false,
					width: 150,
					filter: null,
					dataCell: InvoiceNumberCellData,
					onDeleteClick: async item =>
						this.openConfirmationModal(this.getDeleteInvoiceNumberConfirmModalProps(item.invoiceNumber)),
				},
				{
					key: 'sku',
					title: 'SHIPMENTCOST.SKU',
					selected: true,
					sortable: false,
					width: 150,
					filter: null,
				},
				{
					key: 'warehouse',
					title: 'SHIPMENTCOST.WAREHOUSE',
					selected: true,
					sortable: false,
					width: 150,
					filter: null,
				},
				{
					key: 'invoiceDate',
					title: 'SHIPMENTCOST.INVOICEDATE',
					dataCell: DateTimeCellData,
					dateFormat: 'L',
					useLocalTime: false,
					selected: true,
					sortable: false,
					width: 150,
					filter: null,
				},
				{
					key: 'quotedRate',
					title: 'SHIPMENTCOST.QUOTEDRATE',
					dataCell: ChargesCellData,
					formatter: this.rootStore.usdFormatter,
					selected: true,
					sortable: false,
					width: 200,
					filter: null,
				},
				{
					key: 'billedRate',
					title: 'SHIPMENTCOST.BILLEDRATE',
					dataCell: ChargesCellData,
					formatter: this.rootStore.usdFormatter,
					selected: true,
					sortable: false,
					width: 200,
					filter: null,
				},
				{
					key: 'difference',
					title: 'SHIPMENTCOST.DIFFERENCE',
					dataCell: ChargesDifferenceCellData,
					formatter: this.rootStore.usdFormatter,
					selected: true,
					sortable: false,
					width: 200,
					filter: null,
				},
			],
			actions: {},
			sortObject: [],
		};
	};

	getDeleteInvoiceNumberConfirmModalProps = invoiceNumber => {
		return {
			onConfirm: async () => await this.deleteInvoiceData(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['SHIPMENTCOST.DELETEINVOICENUMBER', { invoiceNumber: invoiceNumber }],
			data: invoiceNumber,
		};
	};

	@action.bound
	deleteInvoiceData = async invoiceNumber => {
		await this.listStore.deleteInvoice(invoiceNumber);
		await this.parentViewStore.reloadChildViewStores();
	};

	@action.bound
	findShipmentCostTn = async () => {
		this.suspend();
		const { dateRangeStart, dateRangeEnd, searchTrackingNumber, ...otherValues } = this.parentViewStore.form.values();
		this.data = [];
		if (dateRangeStart) {
			try {
				if (this.baasicTableSettingsViewStore == undefined) {
					this.baasicTableSettingsViewStore = new BaasicTableSettingsViewStore(this.rootStore);
				}
				this.queryUtility.setFilterParams(
					this,
					{
						dateStart:
							dateRangeStart == ''
								? null
								: moment(dateRangeStart, moment.localeData().longDateFormat('L'), true).format(),
						dateEnd:
							dateRangeEnd == ''
								? null
								: moment(dateRangeEnd, moment.localeData().longDateFormat('L'), true)
										.set({
											hour: 23,
											minute: 59,
											second: 59,
										})
										.format(), // This will format date with timezone and backend will automatically convert to UTC when parsing
						trackingNumber: searchTrackingNumber.trim(),
						shipmentInfoChargeTypeIds:
							this.parentViewStore.chargeTypeFilter != null && this.parentViewStore.chargeTypeFilter.id != null
								? [this.parentViewStore.chargeTypeFilter.id]
								: null,
						shipmentInfoType: this.parentViewStore.shipmentTypeFilter.selectedValue,
						...otherValues,
					},
					this.tableFilter
				);
				await this.listStore.findTn(this.queryUtility.filter);
				if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
					await this.queryUtility.changePage(1, false);
				}
				this.queryUtility.handleResponse(this.listStore.response);
				this.lastSearchFilter = this.queryUtility.filter; // Used for Export
				this.data = this.listStore.response.item;
				this.queryUtility.setFilterPersistance(this, 'shipment-cost-filter', 'shipment-cost-table-data');
				localStorageHandler.set('shipment-cost-filter', this.queryUtility.filter);
				this.prepareData(this.chargeTypeOptions);
			} catch (err) {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}

		this.resume();
	};

	@action.bound
	prepareData = chargeTypes => {
		this.chargeTypesPresent = [];
		if (this.parentViewStore.chargeTypeFilter != null) {
			this.chargeTypesPresent = chargeTypes.filter(item => {
				return item.value == this.parentViewStore.chargeTypeFilter.id;
			});
		} else {
			_.each(chargeTypes, chargeType => {
				//Check each chargeType for existing records, if not found column won't be added to view
				const chargeTypeContainsData = _.some(this.data, shipmentCost => {
					return _.some(shipmentCost.shipmentInfoCosts, charge => {
						return charge.shipmentInfoChargeTypeId == chargeType.value;
					});
				});

				if (chargeTypeContainsData) {
					this.chargeTypesPresent.push(chargeType);
				}
			});
		}
		this.tableData.columns = this.tableData.columns.slice(0, 9); // First 9 columns will always be present
		this.addChargeTypeColumns(this.tableData.columns, this.chargeTypesPresent);
		this.data = this.mapData(this.data);
	};

	@action.bound
	mapData = shipmentCosts => {
		if (shipmentCosts == null) return;

		return _.map(shipmentCosts, shipmentCost => {
			var dataObject = {
				...shipmentCost,
			};

			if (shipmentCost.shipmentInfoCosts != null && shipmentCost.shipmentInfoCosts.length > 0) {
				_.each(shipmentCost.shipmentInfoCosts, itemCost => {
					dataObject[`${itemCost.shipmentInfoChargeType.name}`] = {
						quoted: itemCost.quoted,
						billed: itemCost.billed,
						difference: itemCost.difference,
					};
				});
			}
			return dataObject;
		}).slice();
	};

	@action.bound
	addChargeTypeColumns(columns, chargeTypes) {
		_.each(chargeTypes, chargeType => {
			columns.push(
				{
					key: `${chargeType.label}.quoted`,
					title: `${chargeType.label} Quoted`,
					width: 200,
					sortable: false,
					selected: true,
					filter: null,
				},
				{
					key: `${chargeType.label}.billed`,
					title: `${chargeType.label} Billed`,
					width: 200,
					sortable: false,
					selected: true,
					filter: null,
				},
				{
					key: `${chargeType.label}.difference`,
					title: `${chargeType.label} Difference`,
					dataCell: ChargesDifferenceCellData,
					formatter: this.rootStore.usdFormatter,
					width: 200,
					sortable: false,
					selected: true,
					filter: null,
				}
			);
		});
	}
}

export default ShipmentCostListViewStore;
